import { toAction } from 'rf-lib';
import * as urls from 'rf-lib/urls';
import { pingBackendWithData } from 'rf-lib/urls';
import * as t from './constants';
import * as gamesAPI from '../../api/games';
import * as groupsAPI from '../../api/groups';
import { startGameRunner } from '../../api/games';

type Dispatch = (action: any) => any;

const handleFetchErrors = (response: any, dispatch: Dispatch) => {
  if (!response.ok) {
    dispatch(toAction(t.START_GAME_FAILURE, { message: 'Error in external fetch' }));
  }
  return response;
};

export const startGame = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.START_GAME));

    return startGameRunner(gameId).then(startRes => dispatch(toAction(t.START_GAME_SUCCESS, startRes)))
      .catch(err => dispatch(toAction(t.START_GAME_FAILURE, err)));
  };

export const reanimate = (dispatch: Dispatch) =>
  () => {
    dispatch(toAction(t.REANIMATE));
  };

export const setCurrentAnimation = (dispatch: Dispatch) =>
  (roundNumber: number) => {
    dispatch(toAction(t.SET_CURRENT_ANIMATION, roundNumber));
  };

export const setIsSpinning = (dispatch: Dispatch) =>
  (roundNumber: number) => {
    dispatch(toAction(t.SET_IS_SPINNING, true));
  };

export const fetchGame = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.FETCH_GAME));

    return gamesAPI.fetchGame(gameId)
      .then((gameData: any) => dispatch(toAction(t.FETCH_GAME_SUCCESS, gameData)))
      .catch((err: any) => dispatch(toAction(t.FETCH_GAME_FAILURE, err)));
  };

export const updateGame = (dispatch: Dispatch) =>
  (gameId: string, game: any) => {
    dispatch(toAction(t.UPDATE_GAME));

    return gamesAPI.updateGame(gameId, game)
      .then((gameData: any) => dispatch(toAction(t.UPDATE_GAME_SUCCESS, gameData)))
      .catch((err: any) => dispatch(toAction(t.UPDATE_GAME_FAILURE, err)));
  };

export const resetUpdateState = (dispatch: Dispatch) =>
  () => {
    dispatch(toAction(t.RESET_UPDATE_GAME_STATE));
  };

export const fetchGames = (dispatch: Dispatch) =>
  (playerId: string) => {
    dispatch(toAction(t.FETCH_GAMES));

    return gamesAPI.fetchGames(playerId)
      .then((gamesData: any) => dispatch(toAction(t.FETCH_GAMES_SUCCESS, gamesData)))
      .catch((err: any) => dispatch(toAction(t.FETCH_GAMES_FAILURE, err)));
  };

export const resetCurrentGame = (dispatch: Dispatch) =>
  () => {
    dispatch(toAction(t.RESET_CURRENT_GAME));
  };

export const addGameListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.ADD_GAME_LISTENER));

    return gamesAPI.onGameChange(
      gameId,
      (game: any) =>
        dispatch(toAction(t.GAME_LISTENER_SUCCESS, game.val())),
      (err: any) =>
        dispatch(toAction(t.GAME_LISTENER_FAILURE, err)),
    );
  };

export const removeGameListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    gamesAPI.removeGameListener(gameId);
    dispatch(toAction(t.REMOVE_GAME_LISTENER));
  };

export const addGameRunnerListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.ADD_GAMERUNNER_LISTENER));

    return gamesAPI.onGameRunnerChange(
      gameId,
      (gameRunner: any) =>
        dispatch(toAction(t.GAMERUNNER_LISTENER_SUCCESS, gameRunner.val())),
      (err: any) =>
        dispatch(toAction(t.GAMERUNNER_LISTENER_FAILURE, err)),
    );
  };

export const removeGameRunnerListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    gamesAPI.removeGameRunnerListener(gameId);
    dispatch(toAction(t.REMOVE_GAMERUNNER_LISTENER));
  };

export const addTransactionListener = (dispatch: Dispatch) =>
  (groupId: string) => {
    dispatch(toAction(t.ADD_TRANSACTION_LISTENER));

    return gamesAPI.onTransactionsChange(
      groupId,
      (snap: any) => {
        return dispatch(toAction(
          t.TRANSACTION_LISTENER_SUCCESS,
          snap.val()
        ));
      },
      (err: any) =>
        dispatch(toAction(t.TRANSACTION_LISTENER_FAILURE, err)),
    );
  };

export const removeTransactionListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    gamesAPI.removeTransactionsChangeListener(gameId);
    dispatch(toAction(t.REMOVE_TRANSACTION_LISTENER));
  };

export const addTicketsListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.ADD_TICKETS_LISTENER));

    return gamesAPI.onTicketsChange(
      gameId,
      (Tickets: any) =>
        dispatch(toAction(t.TICKETS_LISTENER_SUCCESS, Tickets.val())),
      (err: any) =>
        dispatch(toAction(t.TICKETS_LISTENER_FAILURE, err)),
    );
  };

export const removeTicketsListener = (dispatch: Dispatch) =>
  (gameId: string) => {
    gamesAPI.removeTicketsListener(gameId);
    dispatch(toAction(t.REMOVE_TICKETS_LISTENER));
  };

export const handleBuy = (dispatch: Dispatch) =>
  (gameId: string, playerId: string) => {
    gamesAPI.buyTicket(gameId, playerId);
    dispatch(toAction(t.BUY_TICKET));
  };

export const handleSell = (dispatch: Dispatch) =>
  (gameId: string, playerId: string) => {
    gamesAPI.sellTicket(gameId, playerId);
    dispatch(toAction(t.SELL_TICKET));
  };

export const handleSetFree = (dispatch: Dispatch) =>
  (gameId: string, playerId: string) => {
    gamesAPI.setFreeTickets(gameId, playerId);
    dispatch(toAction(t.FREE_TICKET));
  };

export const fillTestData = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.FILL_TEST_DATA));

    return gamesAPI.fillTestData(gameId)
      .then(() => dispatch(toAction(t.FILL_TEST_DATA_SUCCESS, { result: true })))
      .catch((err: any) => dispatch(toAction(t.FILL_TEST_DATA_FAILURE, err)));
  };

export const resetFillTestDataRemoteData = (dispatch: Dispatch) =>
  () => {
    dispatch(toAction(t.RESET_FILL_TEST_DATA_REMOTEDATA));
  };

export const resetGame = (dispatch: Dispatch) =>
  (gameId: string) => {
    dispatch(toAction(t.RESET_GAME));

    return gamesAPI.resetGame(gameId)
      .then(() => dispatch(toAction(t.RESET_GAME_SUCCESS, { result: true })))
      .catch((err: any) => dispatch(toAction(t.RESET_GAME_FAILURE, err)));
  };

export const resetResetGameRemoteData = (dispatch: Dispatch) =>
  () => {
    dispatch(toAction(t.RESET_RESET_GAME_REMOTEDATA));
  };

export const fetchGroupDetails = (dispatch: Dispatch) =>
  (groupID: string) => {
    dispatch(toAction(t.FETCH_GROUP_DETAILS));

    return groupsAPI.fetchGroupDetails(groupID)
      .then((group: any) => dispatch(toAction(t.FETCH_GROUP_DETAILS_SUCCESS, group)))
      .catch((err: any) => dispatch(toAction(t.FETCH_GROUP_DETAILS_FAILURE, err)));
  };
